import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_NavigationBars = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🧭 CSS Navigation Bars</h1>

      <p className="tutorial-intro">
        A <b>navigation bar</b> is a key part of any website. It provides links
        that allow users to move between different pages or sections. Navigation
        bars can be styled using CSS to be horizontal, vertical, sticky, or even
        responsive for mobile devices.
      </p>

      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a", marginBottom: "0.5rem" }}>
          🌟 Why Use Navigation Bars?
        </h2>
        <p>
          Navigation bars improve <b>usability</b> and <b>user experience</b> by
          giving quick access to important pages and making the layout more
          organized.
        </p>
      </div>

      {/* Common Uses */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          📘 Common Uses of Navigation Bars
        </h2>
        <ul style={{ lineHeight: "1.8", marginLeft: "1.2rem" }}>
          <li>Horizontal navigation bar for website headers</li>
          <li>Vertical navigation bar for side menus</li>
          <li>Sticky navigation bars fixed on top</li>
          <li>Responsive navigation bars for mobile</li>
          <li>Highlighting active menu items</li>
        </ul>
      </div>

      {/* Example 1: Horizontal Navbar */}
      <div className="tutorial-section">
        <h2 className="section-title">
          🔹 Example 1: Horizontal Navigation Bar
        </h2>

        <CodeCopyBlock
          code={`ul.navbar {
  list-style-type: none;
  margin: 0;
  padding: 0;
  background: linear-gradient(90deg, #3b82f6, #06b6d4);
  overflow: hidden;
  border-radius: 6px;
}

ul.navbar li {
  display: inline-block;
}

ul.navbar li a {
  color: white;
  padding: 14px 20px;
  text-decoration: none;
  display: block;
  transition: background 0.3s;
}

ul.navbar li a:hover {
  background-color: rgba(255,255,255,0.2);
}`}
        />

        <ul
          className="navbar"
          style={{
            listStyleType: "none",
            margin: 0,
            padding: 0,
            background: "linear-gradient(90deg, #3b82f6, #06b6d4)",
            overflow: "hidden",
            borderRadius: "6px",
            position: "unset",
          }}
        >
          {["Home", "About", "Services", "Contact"].map((item) => (
            <li key={item} style={{ display: "inline-block" }}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  color: "white",
                  padding: "14px 20px",
                  textDecoration: "none",
                  display: "block",
                  transition: "background 0.3s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "rgba(255,255,255,0.2)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 2: Vertical Navbar */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Example 2: Vertical Navigation Bar</h2>

        <CodeCopyBlock
          code={`ul.vertical-navbar {
  list-style-type: none;
  margin: 0;
  padding: 0;
  width: 200px;
  background: linear-gradient(180deg, #10b981, #059669);
  border-radius: 6px;
}

ul.vertical-navbar li a {
  display: block;
  color: white;
  padding: 12px;
  text-decoration: none;
  transition: background 0.3s;
}

ul.vertical-navbar li a:hover {
  background-color: rgba(255,255,255,0.2);
}`}
        />

        <ul
          className="vertical-navbar"
          style={{
            listStyleType: "none",
            margin: 0,
            padding: 0,
            width: "200px",
            background: "linear-gradient(180deg, #10b981, #059669)",
            borderRadius: "6px",
          }}
        >
          {["Home", "About", "Services", "Contact"].map((item) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "block",
                  color: "white",
                  padding: "12px",
                  textDecoration: "none",
                  transition: "background 0.3s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "rgba(255,255,255,0.2)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 3: Responsive Navbar */}
      <div className="tutorial-section">
        <h2 className="section-title">
          🔹 Example 3: Responsive Navigation Bar
        </h2>

        <CodeCopyBlock
          code={`.responsive-navbar {
  background: linear-gradient(90deg, #f97316, #ea580c);
  overflow: hidden;
  border-radius: 6px;
}

.responsive-navbar a {
  float: left;
  display: block;
  color: white;
  padding: 14px 20px;
  text-decoration: none;
  transition: background 0.3s;
}

.responsive-navbar a:hover {
  background-color: rgba(255,255,255,0.2);
}

@media screen and (max-width: 600px) {
  .responsive-navbar a {
    float: none;
    display: block;
    text-align: center;
  }
}`}
        />

        <div
          className="responsive-navbar"
          style={{
            background: "linear-gradient(90deg, #f97316, #ea580c)",
            overflow: "hidden",
            borderRadius: "6px",
          }}
        >
          {["Home", "About", "Services", "Contact"].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              style={{
                float: "left",
                display: "block",
                color: "white",
                padding: "14px 20px",
                textDecoration: "none",
                transition: "background 0.3s",
              }}
              onMouseEnter={(e) =>
                (e.target.style.backgroundColor = "rgba(255,255,255,0.2)")
              }
              onMouseLeave={(e) =>
                (e.target.style.backgroundColor = "transparent")
              }
            >
              {item}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CSS_NavigationBars;
